import React, { Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import * as THREE from "three";
import { OrbitControls, useGLTF, Html, Environment, ContactShadows } from "@react-three/drei";

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

function Model({ url, onLoad }) {
    const { scene } = useGLTF(url);

    useEffect(() => {
        if (!scene) return;

        const box = new THREE.Box3().setFromObject(scene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // 🔥 Centrar modelo
        scene.position.sub(center);

        // 🔥 Escalar automático
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        scene.scale.setScalar(scale);

        if (onLoad) onLoad(scene);

        scene.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });

    }, [scene, onLoad]);

    return <primitive object={scene} />;
}

function AutoCamera({ scene }) {
    const { camera } = useThree();

    useEffect(() => {
        if (!scene) return;

        scene.updateMatrixWorld(true);

        const box = new THREE.Box3().setFromObject(scene);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);

        let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        cameraZ *= 1.5;

        camera.position.set(0, 0, cameraZ);

        camera.lookAt(center);

        camera.updateProjectionMatrix();

    }, [scene, camera]);

    return null;
}

function Loader() {
    return (
        <Html center>
            <div className="flex flex-col items-center gap-2">
                <div className="w-40 h-2 bg-gray-300 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 animate-pulse w-full"></div>
                </div>
                <p className="text-sm text-gray-600">Cargando modelo 3D...</p>
            </div>
        </Html>
    );
}

const ProductViewer3D = ({ model }) => {
    const [sceneRef, setSceneRef] = React.useState(null);
    if (!model) return null;

    return (
        <div className="w-full h-80 bg-gray-100 rounded-xl overflow-hidden">
            <Canvas
                shadows
                camera={{ position: [0, 0, 2], fov: 50 }}
                gl={{ physicallyCorrectLights: true }}
            >
                <OrbitControls
                    enableZoom={true}
                    target={[0, 0, 0]}
                    autoRotate
                    autoRotateSpeed={1}
                />
                <ambientLight intensity={0.1} />
                <Environment preset="studio" />
                <ContactShadows
                    position={[0, -1, 0]}
                    opacity={0.5}
                    scale={10}
                    blur={2}
                    far={5}
                />
                <directionalLight position={[2, 2, 2]} />

                <Suspense fallback={<Loader />}>
                    <Model url={model} onLoad={setSceneRef} />
                    {sceneRef && <AutoCamera scene={sceneRef} />}
                </Suspense>

            </Canvas>
        </div>
    );
};

export default ProductViewer3D;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.